import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { base64toBlob as base64ToBlob_ } from '@/components/App/Approvement/helpers';

import { createNumberFormatter } from '../numberFormatter';

/**
 * Конвертирует объект Blob в строку base64.
 *
 * @param {Blob} blob - Объект Blob, который нужно конвертировать.
 * @returns {Promise<string>} - Промис, который разрешается в строку base64.
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

/**
 * Конвертирует строку base64 в объект Blob.
 *
 * @param {string} base64 - Строка в формате base64.
 * @param {string} [mimeType='application/octet-stream'] - MIME-тип для создаваемого Blob.
 * @returns {Blob} - Объект Blob, созданный из строки base64.
 */
export const base64ToBlob = (
  base64: string,
  mimeType: string = 'application/octet-stream'
): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

/**
 * Конвертирует буфер в строку base64.
 *
 * @param {any} buffer - Буфер, который нужно конвертировать.
 * @returns {string} - Строка в формате base64, созданная из буфера.
 */
export const bufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
};

/**
 * Генерирует временный URL-объект из base64-строки PDF-файла.
 *
 * @param base64 - base64-строка без префикса (только данные).
 * @returns URL-объект, пригодный для открытия в новом окне/вкладке.
 */
export const getObjectUrl = (base64: string): string => {
  if (!base64) return '';
  return URL.createObjectURL(
    base64ToBlob_(`data:application/pdf;base64,${base64}`, 'application/pdf')
  );
};

/**
 * Открывает PDF-файл в новой вкладке браузера.
 *
 * @param file - Объект с `pdfUrl` и `name` для отображения PDF в новой вкладке.
 */
export const openPdf = (file: { pdfUrl: string; name: string }) => {
  if (!file.pdfUrl) return;
  const pdfWindow = window.open('', '_blank');
  if (pdfWindow) {
    pdfWindow.location.href = file.pdfUrl;
    pdfWindow.document.title = file.name;
  }
};

/**
 * Конвертирует файл в строку base64.
 *
 * @param {File} file - Файл, который нужно конвертировать.
 * @returns {Promise<string>} - Промис, который разрешается в строку base64.
 */
export const convertFileToBase64 = async (
  file: File
): Promise<[string, string]> => {
  try {
    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
    const parts = base64String.split(',');
    return [parts[0] ?? '', parts[1] ?? ''];
  } catch {
    return ['', ''];
  }
};

/**
 * Сохраняет строку base64 как PDF-файл.
 *
 * @param {string} base64String - Строка в формате base64, представляющая PDF.
 * @param {string} fileName - Имя файла для сохранения.
 */
export const saveBase64AsPDF = (base64String: string, fileName: string) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

  saveAs(pdfBlob, fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
};

/**
 * Сохраняет строку base64 как изображение.
 *
 * @param {string} base64String - Строка в формате base64, представляющая изображение.
 * @param {string} fileName - Имя файла для сохранения (с расширением).
 */
export const saveBase64AsImage = (base64String: string, fileName: string) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const imageBlob = new Blob([byteArray], { type: 'image/webp' });

  saveAs(imageBlob, fileName);
};

/** Человекочитаемый размер файла: `10 МБ`, `500 КБ` и т.д. */
export const formatFileSize = (
  bytes: number,
  language: 'ru' | 'en' = 'ru'
): string => {
  const UNITS =
    language === 'ru' ? ['Б', 'КБ', 'МБ', 'ГБ'] : ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  const formatter = createNumberFormatter();

  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  return `${formatter(value).formattedValue} ${UNITS[unitIndex]}`;
};

/**
 * Функция addExtensionIfMissing проверяет, содержит ли название файла расширение.
 * Если расширение отсутствует, функция добавляет его из ссылки на файл и возвращает обновленное имя.
 *
 * @param {Object} file - Объект с информацией о файле.
 * @param {string} file.name - Название файла без расширения или с ним.
 * @param {string} file.url - Ссылка на файл, содержащая расширение.
 *
 * @returns {string} - Название файла с расширением, если оно отсутствовало, или оригинальное название.
 */
export const addExtensionIfMissing = (file: {
  name: string;
  url: string;
}): string => {
  // Регулярное выражение для получения расширения из ссылки (после последней точки)
  const urlExtensionMatch = file.url.match(/\.(\w+)(?:\?|#|$)/);
  const urlExtension = urlExtensionMatch ? urlExtensionMatch[1] : null;

  // Проверка, есть ли расширение в названии файла
  const hasExtension = /\.\w+$/.test(file.name);

  // Если расширение в названии отсутствует и оно найдено в ссылке, добавляем его
  if (!hasExtension && urlExtension) {
    return `${file.name}.${urlExtension}`;
  }

  return file.name;
};

/**
 * Асинхронно разархивирует ZIP-файл и возвращает содержимое файлов.
 * @param file - ZIP-файл для разархивирования (File из input или Blob из fetch).
 * @returns Промис, возвращающий массив объектов с именами файлов и их содержимым в формате Base64.
 * @example
 * ```typescript
 * const files = await unzipFile(zipFile);
 * ```
 */
export const unzipFile = async (file: File | Blob) => {
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(file);

    const unzippedFiles: Array<{ name: string; content: Blob }> = [];

    // Перебираем файлы в архиве
    for (const fileName in zipContent.files) {
      const file = zipContent.files[fileName];
      if (!file.dir) {
        // Извлекаем содержимое как blob
        const content = await file.async('blob');
        unzippedFiles.push({ name: fileName, content });
      }
    }

    return unzippedFiles;
  } catch (e) {
    const error = e as Error;
    throw new Error(`Ошибка при разархивировании: ${error.message}`, {
      cause: e
    });
  }
};

/**
 * Конвертирует массив файлов в ZIP-файл.
 * @param files - Массив файлов для конвертации.
 * @returns Промис, возвращающий ZIP-файл в формате Blob.
 */
export const filesToZip = async (
  files: Array<{ name: string; content: Blob }>
) => {
  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.name, file.content);
  }
  return zip.generateAsync({ type: 'blob' });
};

export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Ошибка при конвертации blob в base64'));
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

/**
 * Вычисляет размер base64-строки в байтах.
 * @param base64 - Base64-строка.
 * @returns Размер в байтах.
 */
export const getBase64Size = (base64: string): number => {
  const str = base64.includes(',') ? base64.split(',')[1] : base64;

  let padding = 0;
  if (str.endsWith('==')) padding = 2;
  else if (str.endsWith('=')) padding = 1;

  return (str.length * 3) / 4 - padding;
};
